import type { paths } from '../../api/generated/schema'
import { formatCents } from '../../api/parseMoney'

/**
 * The list's filters. They live in the URL, so a filtered view can be shared
 * and Back walks through the filters a user applied.
 *
 * Single-select and mapped 1:1 onto server parameters (Q14): nothing here is
 * filtered client-side, so `total`, the pager and every day subtotal describe
 * what the server returned and nothing else.
 */
export type TxnFilters = {
  /** `YYYY-MM`; null is all time. */
  month: string | null
  /** Merchant search (Q6). Sent as `search`. */
  q: string
  accountId: string | null
  categoryId: string | null
  ownerId: string | null
  tag: string | null
  /** Cents, expense-positive: "Spent between" (Q14). Both map to `min_amount`/`max_amount`. */
  spentMin: number | null
  spentMax: number | null
  /** `null` is any. */
  pending: boolean | null
  review: boolean
  page: number
}

export const PAGE_SIZE = 50

export type TxnQuery = NonNullable<paths['/api/v1/transactions']['get']['parameters']['query']>

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/
const CENTS = /^\d{1,15}$/
const PAGE = /^[1-9]\d{0,8}$/

function monthOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function textOf(v: string | null): string | null {
  return v !== null && v !== '' ? v : null
}

function centsOf(v: string | null): number | null {
  return v !== null && CENTS.test(v) ? Number(v) : null
}

export function readFilters(params: URLSearchParams, today: Date): TxnFilters {
  const rawMonth = params.get('month')
  const month = rawMonth === 'all' ? null : rawMonth !== null && MONTH.test(rawMonth) ? rawMonth : monthOf(today)
  const rawPage = params.get('page')
  const pending = params.get('pending')
  return {
    month,
    q: params.get('q') ?? '',
    accountId: textOf(params.get('account')),
    categoryId: textOf(params.get('category')),
    ownerId: textOf(params.get('owner')),
    tag: textOf(params.get('tag')),
    spentMin: centsOf(params.get('min')),
    spentMax: centsOf(params.get('max')),
    pending: pending === 'true' ? true : pending === 'false' ? false : null,
    review: params.get('review') === '1',
    page: rawPage !== null && PAGE.test(rawPage) ? Number(rawPage) : 1,
  }
}

/**
 * Omits every default. The current-month default depends on today, so passing
 * `today` lets the current month be omitted too; without it the month is
 * always written, which is correct but longer.
 */
export function writeFilters(f: TxnFilters, today?: Date): URLSearchParams {
  const p = new URLSearchParams()
  if (f.month === null) p.set('month', 'all')
  else if (today === undefined || f.month !== monthOf(today)) p.set('month', f.month)
  if (f.q !== '') p.set('q', f.q)
  if (f.accountId !== null) p.set('account', f.accountId)
  if (f.categoryId !== null) p.set('category', f.categoryId)
  if (f.ownerId !== null) p.set('owner', f.ownerId)
  if (f.tag !== null) p.set('tag', f.tag)
  if (f.spentMin !== null) p.set('min', String(f.spentMin))
  if (f.spentMax !== null) p.set('max', String(f.spentMax))
  if (f.pending !== null) p.set('pending', String(f.pending))
  if (f.review) p.set('review', '1')
  if (f.page !== 1) p.set('page', String(f.page))
  return p
}

/** The first and last day of `YYYY-MM`. `end_date` is inclusive on the server (`<=`). */
export function monthBounds(month: string): { start: string; end: string } {
  const m = MONTH.exec(month)
  if (!m) throw new Error(`monthBounds: not a YYYY-MM month: ${JSON.stringify(month)}`)
  const year = Number(m[1])
  const mon = Number(m[2])
  // Day 0 of the following month is the last day of this one; UTC so no local
  // offset can move the date.
  const last = new Date(Date.UTC(year, mon, 0)).getUTCDate()
  return { start: `${month}-01`, end: `${month}-${String(last).padStart(2, '0')}` }
}

export function toQuery(f: TxnFilters): TxnQuery {
  const q: TxnQuery = { page: f.page, page_size: PAGE_SIZE }
  if (f.month !== null) {
    const { start, end } = monthBounds(f.month)
    q.start_date = start
    q.end_date = end
  }
  const search = f.q.trim()
  if (search !== '') q.search = search
  if (f.accountId !== null) q.account_id = f.accountId
  if (f.categoryId !== null) q.category_id = f.categoryId
  if (f.ownerId !== null) q.beneficial_owner_id = f.ownerId
  if (f.tag !== null) q.tag = f.tag
  // `formatCents`, never `/ 100`: a float quotient is how 0.29 becomes 0.28999….
  if (f.spentMin !== null) q.min_amount = formatCents(f.spentMin)
  if (f.spentMax !== null) q.max_amount = formatCents(f.spentMax)
  if (f.pending !== null) q.is_pending = f.pending
  if (f.review) q.review_status = 'pending'
  return q
}

/** Month, search text and page are not "filters" a user clears with Clear filters. */
export function activeFilterCount(f: TxnFilters): number {
  return (
    (f.accountId !== null ? 1 : 0) +
    (f.categoryId !== null ? 1 : 0) +
    (f.ownerId !== null ? 1 : 0) +
    (f.tag !== null ? 1 : 0) +
    (f.spentMin !== null || f.spentMax !== null ? 1 : 0) +
    (f.pending !== null ? 1 : 0) +
    (f.review ? 1 : 0)
  )
}

/** Everything except the page. A selection is cleared, with a notice, when this changes. */
export function filterIdentity(f: TxnFilters): string {
  const { page: _page, ...rest } = f
  return JSON.stringify(rest)
}
